import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { industriesAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { 
  Eye, 
  EyeOff, 
  Loader2, 
  Mail, 
  Lock, 
  User, 
  Building, 
  Briefcase, 
  AlertCircle, 
  CheckCircle,
  Sparkles,
  Award,
  Users,
  BookOpen
} from 'lucide-react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

// Logo SVG Component - Updated to match header
const Logo = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 280 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 27 L28 8 L46 27 L28 19 L10 27Z" fill="#1B4F72" />
    <path d="M28 19 L46 27 L28 46 L28 36 L16 27 L28 19Z" fill="#D4A03A" fillOpacity="0.9" />
    <text x="56" y="35" fontFamily="Space Grotesk, sans-serif" fontSize="26" fontWeight="700" fill="#1B4F72">GRC</text>
    <text x="110" y="35" fontFamily="Space Grotesk, sans-serif" fontSize="26" fontWeight="700" fill="#D4A03A">EDUCATORS</text>
    <text x="56" y="50" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="9" fontWeight="500" fill="#64748B" letterSpacing="0.1em">YOUR PATH TO EXCELLENCE</text>
  </svg>
);

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    company: '',
    job_title: '',
    industry_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    fetchIndustries();
  }, [isAuthenticated, navigate]);

  const fetchIndustries = async () => {
    try {
      const response = await industriesAPI.getAll();
      setIndustries(response.data);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({ ...prev, phone }));
  };

  const handleIndustryChange = (value) => {
    setFormData((prev) => ({ ...prev, industry_id: value }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    
    const { confirm_password, ...registerData } = formData;
    const result = await register(registerData);
    
    if (result.success) {
      // Redirect to dashboard after successful registration
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const passwordRequirements = [
    { text: '8+ chars', met: formData.password.length >= 8 },
    { text: 'Uppercase', met: /[A-Z]/.test(formData.password) },
    { text: 'Lowercase', met: /[a-z]/.test(formData.password) },
    { text: 'Number', met: /[0-9]/.test(formData.password) },
    { text: 'Special', met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password) },
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-start justify-center p-6 md:p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-md pt-10">
          <div className="mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-950 mb-2">
              Create Your Account
            </h1>
            <p className="text-zinc-500 text-sm">
              Join thousands of professionals advancing their GRC careers
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="bg-red-50 border-red-200 rounded-xl">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="first_name" className="text-zinc-700 text-sm">
                  First Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="John"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="pl-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                    required
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="last_name" className="text-zinc-700 text-sm">
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  placeholder="Smith"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-zinc-700 text-sm">
                Work Email *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Phone Number with Country Code */}
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-zinc-700 text-sm">
                Phone Number *
              </Label>
              <PhoneInput
                defaultCountry="us"
                value={formData.phone}
                onChange={handlePhoneChange}
                inputClassName="!w-full !h-10 !rounded-lg !border-zinc-200 focus:!border-zinc-900 !text-sm"
                countrySelectorStyleProps={{
                  buttonClassName: "!h-10 !rounded-l-lg !border-zinc-200 !bg-zinc-50 hover:!bg-zinc-100",
                }}
                className="phone-input-custom"
                data-testid="phone-input"
              />
            </div>

            {/* Company & Job Title */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="company" className="text-zinc-700 text-sm">
                  Company
                </Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="company"
                    name="company"
                    placeholder="Company Inc."
                    value={formData.company}
                    onChange={handleChange}
                    className="pl-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                    data-testid="company-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="job_title" className="text-zinc-700 text-sm">
                  Job Title
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="job_title"
                    name="job_title"
                    placeholder="Manager"
                    value={formData.job_title}
                    onChange={handleChange}
                    className="pl-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                    data-testid="job-title-input"
                  />
                </div>
              </div>
            </div>

            {/* Industry */}
            <div className="space-y-1.5">
              <Label htmlFor="industry_id" className="text-zinc-700 text-sm">
                Industry
              </Label>
              <SearchableSelect
                options={industries.map((industry) => ({ value: industry.id, label: industry.name }))}
                value={formData.industry_id}
                onValueChange={handleIndustryChange}
                placeholder="Select your industry"
                searchPlaceholder="Search industries..."
                className="h-10 rounded-lg border-zinc-200 text-sm"
                dataTestId="industry-select"
                preserveOrder={true}
              />
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-zinc-700 text-sm">
                  Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-9 pr-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm_password" className="text-zinc-700 text-sm">
                  Confirm *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className="pl-9 h-10 rounded-lg border-zinc-200 focus:border-zinc-900 text-sm"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            {formData.password && (
              <div className="flex items-center gap-2 text-xs">
                {passwordRequirements.map((req, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    {req.met ? (
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-zinc-300" />
                    )}
                    <span className={req.met ? 'text-green-600' : 'text-zinc-500'}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white font-semibold rounded-md transition-colors"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-xs text-center text-zinc-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms-of-use" className="text-zinc-900 hover:underline">
                Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="text-zinc-900 hover:underline">
                Privacy Policy
              </Link>
            </p>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-zinc-500">Already have an account? </span>
            <Link
              to="/login"
              className="text-zinc-900 hover:text-emerald-700 font-semibold"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Image/Branding */}
      <div className="hidden lg:flex flex-1 bg-[#09090b] relative overflow-hidden">
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-center">
          <div className="mb-8">
            <Sparkles className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Start Your Learning Journey
            </h2>
            <p className="text-zinc-400 text-base max-w-md">
              Access world-class GRC training from industry experts and earn valuable CPE credits.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4 mt-8 max-w-sm">
            {[
              { icon: BookOpen, text: 'Expert-led webinars & seminars' },
              { icon: Award, text: 'Earn CPE credits & certificates' },
              { icon: Users, text: 'Join 15,000+ professionals' },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-white/90 text-sm">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-12">
            {[
              { value: '6K+', label: 'Webinars' },
              { value: '700+', label: 'Speakers' },
              { value: '50+', label: 'Industries' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl font-bold text-emerald-400 font-heading">{stat.value}</div>
                <div className="text-zinc-400 text-xs mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom styles for phone input */}
      <style>{`
        .phone-input-custom .react-international-phone-input-container {
          width: 100%;
        }
        .phone-input-custom .react-international-phone-input {
          width: 100% !important;
          height: 40px !important;
          border-radius: 0 8px 8px 0 !important;
          border-color: #e2e8f0 !important;
          font-size: 14px !important;
        }
        .phone-input-custom .react-international-phone-input:focus {
          border-color: #1B4F72 !important;
          box-shadow: 0 0 0 1px #1B4F72 !important;
        }
        .phone-input-custom .react-international-phone-country-selector-button {
          height: 40px !important;
          border-radius: 8px 0 0 8px !important;
          border-color: #e2e8f0 !important;
          background-color: #f8fafc !important;
        }
        .phone-input-custom .react-international-phone-country-selector-button:hover {
          background-color: #f1f5f9 !important;
        }
      `}</style>
    </div>
  );
}
